import { WRAPPED_SOL_MINT } from "@project-serum/serum/lib/token-instructions";

import * as anchor from "@malloc-protocol/anchor";
import {
  MallocActionBuilderError,
  MallocError,
  MallocErrorTypes,
  MallocRunnerError,
  MallocSdk,
  RunConstructionOpts,
  utils,
} from "@malloc-protocol/sdk";

import { MintLayout, TOKEN_PROGRAM_ID, Token } from "@solana/spl-token";
import { getConstants } from "../config/constants";
import { PublicKey } from "@solana/web3.js";
import {
  ErrorState,
  errorStore,
  globalStore,
  MallocState,
} from "../globalStore";
import { MallocSPLBuildActionMap } from "@malloc-protocol/spl";
import { AnchorWallet } from "@solana/wallet-adapter-react";
import {
  getConstructionSignerFromStorage,
  saveConstructionSigner,
} from "./localStorage";
import { setErrorMessage } from "./errors";

const SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID = new anchor.web3.PublicKey(
  "ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL"
);

const TOKEN_METADATA_PROGRAM_ID = new anchor.web3.PublicKey(
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s"
);

export const awaitTransactionSignatureConfirmation = async (
  txid: anchor.web3.TransactionSignature,
  timeout: number,
  connection: anchor.web3.Connection,
  commitment: anchor.web3.Commitment = "recent",
  queryStatus = false
): Promise<anchor.web3.SignatureStatus | null | void> => {
  let done = false;
  let status: anchor.web3.SignatureStatus | null | void = {
    slot: 0,
    confirmations: 0,
    err: null,
  };
  let subId = 0;
  status = await new Promise(async (resolve, reject) => {
    setTimeout(() => {
      if (done) {
        return;
      }
      done = true;
      console.log("Rejecting for timeout...");
      reject({ timeout: true });
    }, timeout);
    try {
      subId = connection.onSignature(
        txid,
        (result: any, context: any) => {
          done = true;
          status = {
            err: result.err,
            slot: context.slot,
            confirmations: 0,
          };
          if (result.err) {
            console.log("Rejected via websocket", result.err);
            reject(status);
          } else {
            console.log("Resolved via websocket", result);
            resolve(status);
          }
        },
        commitment
      );
    } catch (e) {
      done = true;
      console.error("WS error in setup", txid, e);
    }
    while (!done && queryStatus) {
      // eslint-disable-next-line no-loop-func
      (async () => {
        try {
          const signatureStatuses = await connection.getSignatureStatuses([
            txid,
          ]);
          status = signatureStatuses && signatureStatuses.value[0];
          if (!done) {
            if (!status) {
              console.log("REST null result for", txid, status);
            } else if (status.err) {
              console.log("REST error for", txid, status);
              done = true;
              reject(status.err);
            } else if (!status.confirmations) {
              console.log("REST no confirmations for", txid, status);
            } else {
              console.log("REST confirmation for", txid, status);
              done = true;
              resolve(status);
            }
          }
        } catch (e) {
          if (!done) {
            console.log("REST connection error: txid", txid, e);
          }
        }
      })();
      await sleep(2000);
    }
  });

  //@ts-ignore
  if (connection._signatureSubscriptions[subId]) {
    connection.removeSignatureListener(subId);
  }
  done = true;
  console.log("Returning status", status);
  return status;
};

/* export */ const createAssociatedTokenAccountInstruction = (
  associatedTokenAddress: anchor.web3.PublicKey,
  payer: anchor.web3.PublicKey,
  walletAddress: anchor.web3.PublicKey,
  splTokenMintAddress: anchor.web3.PublicKey
) => {
  const keys = [
    { pubkey: payer, isSigner: true, isWritable: true },
    { pubkey: associatedTokenAddress, isSigner: false, isWritable: true },
    { pubkey: walletAddress, isSigner: false, isWritable: false },
    { pubkey: splTokenMintAddress, isSigner: false, isWritable: false },
    {
      pubkey: anchor.web3.SystemProgram.programId,
      isSigner: false,
      isWritable: false,
    },
    { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    {
      pubkey: anchor.web3.SYSVAR_RENT_PUBKEY,
      isSigner: false,
      isWritable: false,
    },
  ];
  return new anchor.web3.TransactionInstruction({
    keys,
    programId: SPL_ASSOCIATED_TOKEN_ACCOUNT_PROGRAM_ID,
    data: Buffer.from([]),
  });
};

/**
 * Build the construction and fill in input/ out mint data on failure
 */
export const buildConstructionAndFill = async () => {
  console.log("Partial build started");
  console.log(globalStore.state.construction);
  try {
    if (globalStore.state.malloc) {
      const ret = await globalStore.state.malloc.buildWithFill(
        globalStore.state.construction,
        MallocSPLBuildActionMap
      );
      console.log("Partial build ended");
      const state = globalStore.state;
      state.construction.actionDatas = ret.filledNonSpecific.actionDatas;
      globalStore.setState({
        ...state,
      });
    }
  } catch (e: any) {
    if (e?.type === MallocErrorTypes.MALLOC_ACTION_BUILDER) {
      const filled = (e as MallocActionBuilderError)
        .filledNonSpecificConstruction;
      if (filled) {
        const state = globalStore.state;
        state.construction = filled;
        globalStore.setState({
          ...state,
        });
      }
    }
    console.log("Partial build ended");
    throw e;
  }
};

export const setMallocError = (
  e: any | MallocError,
  state: MallocState,
  currErrorState: ErrorState
) => {
  console.error("Error building flow", e);
  let actionName: string | undefined = currErrorState.failedAction;
  if (
    (e as MallocError).mallocErrorMarker &&
    (e as MallocError).type === MallocErrorTypes.MALLOC_RUNNER
  ) {
    actionName = (e as MallocRunnerError).actionName;
    if (actionName) {
      state.context.nodes[actionName].failedRunning = true;
      state.context.nodes[actionName].successfullyRan = false;
    }
  }

  state.loading = undefined;
  globalStore.setState({
    ...state,
  });
  setErrorMessage(
    e?.message || `An unexpected error occurred: ${e}`,
    currErrorState,
    actionName
  );
};

export const tryPartialBuild = async (state: MallocState) => {
  try {
    await buildConstructionAndFill();
  } catch (e: any) {
    setMallocError(e, state, errorStore.state);
  }
};

export const shortenAddress = (address: string, chars = 4): string => {
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
};

const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getMallocSdk = (wallet: anchor.Provider): MallocSdk => {
  return new MallocSdk(
    new PublicKey(getConstants().MALLOC_PROGRAM_ID || ""),
    wallet,
    "mainnet-beta"
  );
};

const getRunOpts = (state: MallocState): RunConstructionOpts => {
  return {
    onTxDone: (curr, total, actionNames) => {
      if (actionNames) {
        console.log("ACTIONS SUCCESSFULLY RAN", actionNames);
        actionNames.map((actionSuccess) => {
          state.context.nodes[actionSuccess].successfullyRan = true;
          state.context.nodes[actionSuccess].failedRunning = false;
        });
      }
      state.loading = {
        progress: curr,
        max: total,
      };
      globalStore.setState(state);
    },

    retryTxOnTimeout: 1,
    customTxTimeoutMs: 20 * 1000,
    commitment: {
      skipPreflight: true,
      commitment: "single",
    },
  };
};

export const runFlow = async (
  state: MallocState,
  wallet: AnchorWallet,
  setLoading: (v: boolean) => void,
  rerunFromAction?: string
) => {
  if (!state.malloc) {
    alert("Please connect your wallet before running a flow");
    return;
  }
  window.onbeforeunload = function (e: BeforeUnloadEvent) {
    e = e || window.event;

    const message = "Exiting may prematurely end your flow";

    // For IE6-8 and Firefox prior to version 4
    if (e) {
      e.returnValue = message;
    }
    e.preventDefault()

    // For Chrome, Safari, IE8+ and Opera 12+
    return message;
  };
  setLoading(true);
  const initialMints = Object.keys(state.construction.source.mints);
  const initTokenAccounts = await Promise.all(
    initialMints.map(async (mint) => {
      // If the initial mint is a wrapped sol mint, then just transfer from the wallet
      if (WRAPPED_SOL_MINT.equals(new PublicKey(mint))) return wallet.publicKey;
      return utils.tokens.getTokenAccountUsedByMalloc(wallet.publicKey, mint);
    })
  );

  // Reset all the actions which ran successfully
  if (!rerunFromAction) {
    Object.keys(state.construction.actionDatas).map((actionName) => {
      state.context.nodes[actionName].successfullyRan = false;
      state.context.nodes[actionName].failedRunning = false;
    });
    globalStore.setState(state);
  }

  try {
    if (rerunFromAction) {
      const constrSigner = getConstructionSignerFromStorage();
      if (!constrSigner) {
        alert("Cannot rerun a flow which has yet to run");
        setLoading(false);
        return;
      }
      const built = await state.malloc.build(
        state.construction,
        MallocSPLBuildActionMap,
        { cleanupAtEnd: true }
      );
      const compiledInst = await state.malloc.compileConstruction({
        construction: built,
        actionMap: MallocSPLBuildActionMap,
        amountInAccounts: initTokenAccounts,
        opts: {
          cleanupAtEnd: true,
          constructionAccount: constrSigner,
        },
      });
      await state.malloc.runConstructionFromAction(
        compiledInst,
        rerunFromAction,
        getRunOpts(state)
      );
    } else {
      const built = await state.malloc.build(
        state.construction,
        MallocSPLBuildActionMap,
        { cleanupAtEnd: true }
      );
      const compiledInst = await state.malloc.compileConstruction({
        construction: built,
        actionMap: MallocSPLBuildActionMap,
        amountInAccounts: initTokenAccounts,
        opts: { cleanupAtEnd: true },
      });
      saveConstructionSigner(compiledInst.constructionSigner);
      await state.malloc.runConstruction(compiledInst, getRunOpts(state));
    }
    alert("Flow ran successfully!");

    state.loading = undefined;
    globalStore.setState(state);

    // Reset the failed action status
    errorStore.setState({
      ...errorStore.state,
      failedAction: undefined,
    });
  } catch (e: any | MallocError) {
    console.error("An error occurred running the flow", e);
    setMallocError(e, state, errorStore.state);
  }

  window.onbeforeunload = null;
  setLoading(false);
};

export const deleteAction = (id: string) => {
  const state = globalStore.state;
  // delete all edges linked to selected node
  for (let [node_id, node] of Object.entries(state.context.nodes)) {
    if (node_id != id) {
      if (node.outEdges[id]) {
        delete node.outEdges[id];

        if (node.isSource) {
          delete state.construction.source.nextActions[id];
        } else {
          // @ts-ignore
          // delete state.construction.actionDatas[node_id].action.nextActions[id];
          delete (state.construction.actionDatas[node_id] as any).action
            .nextActions[state.context.selectedOutMintIndex][id];
        }
      }
    }
  }

  // delete node
  delete state.context.nodes[id];
  delete state.construction.actionDatas[id];
  state.context.selectedNodeId = "SOURCE";
  globalStore.setState({
    ...state,
  });
};
